import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';
import type {
    CellValueChangedEvent,
    CellEditingStoppedEvent,
    CellDoubleClickedEvent,
} from 'ag-grid-community/dist/types/core/events';
import { useTranslation } from 'react-i18next';
import TableGridItem from '../UI/TableGridItem';
import { BillingConfigWithRelations } from '../../services/budgetBillingConfig';
import { PendingCellEdits } from '../../hooks/usePendingCellEdits';
import { useBudgetContext } from '../../store/Budget/BudgetContext';
import { useOverviewMonths } from './useOverviewMonths';
import { useRowStatus } from './useRowStatus';
import { useBudgetPeriodMonths } from './useBudgetPeriodMonths';
import {
    generateMonthColumns,
    formatMonthRange,
    formatWholeEuro,
    integerFormatter,
    parseBudgetCell,
    MONTH_KEY,
} from './helpers';
import { buildExtrapolatedRow, calculateAllCosts, RowAckInfo } from './budgetCalculation';
import {
    EDITABLE_CELL_BG,
    EXTRAPOLATED_CELL_BG,
    MANUAL_ACCENT_SHADOW,
    ORPHANED_ROW_BG,
    pendingBorder,
} from './pendingCellStyle';
import RowStatusCell from './RowStatusCell';
import ConfigDetailDialog, { ConfigDetailDialogData } from './ConfigDetailDialog';

export type HandlingView = 'values' | 'kosten';

/** The pending kind this grid writes — also the field the batch payload carries it in. */
export const FORECAST_EDIT_KIND = 'forecastVolumeEdits';

interface ForecastVolumeGridProps {
    billingConfigs: BillingConfigWithRelations[];
    isLoading: boolean;
    view: HandlingView;
    /** The Overview's pending edits — this grid records into them; the page saves them (CALL-779). */
    pending: PendingCellEdits;
    /** CALL-781: hide month COLUMNS before the current month. Display only — see `months` below. */
    hidePastMonths?: boolean;
}

const ForecastVolumeGrid = ({
    billingConfigs,
    isLoading,
    view,
    pending,
    hidePastMonths = false,
}: ForecastVolumeGridProps) => {
    const gridRef = useRef<AgGridReact>(null);
    const { t } = useTranslation('budget');
    const { isReadOnly, showExtrapolation, selectedVersionId } = useBudgetContext();

    // CALL-816: the row verdict AND its acknowledgement state, both over the funded budget period —
    // see useRowStatus. Deliberately NOT derived in the row build below, which re-runs on every cell
    // edit, and deliberately not over `months` (the view) any more: the tooltip counted the window
    // while the dialog listed the whole period.
    const rowStatus = useRowStatus(selectedVersionId);
    // The same period as a caption, so a tooltip counting 25 months says WHICH 25. Derived once here and
    // handed to the cell renderer, rather than re-derived per cell.
    const periodMonths = useBudgetPeriodMonths(selectedVersionId);
    const periodLabel = periodMonths.length
        ? formatMonthRange(periodMonths[0], periodMonths[periodMonths.length - 1])
        : undefined;
    // CALL-818: the per-config month editor behind each row's status icon (and row double-click) —
    // also where CALL-816's warning is acknowledged, so the icon leads to both answers at once.
    const [detailDialog, setDetailDialog] = useState<ConfigDetailDialogData | null>(null);
    // Switching ?versionId= swaps the page's data under an open dialog without remounting anything
    // (config ids are global) — its captured config and edits would belong to the OLD version while
    // its save targeted the new one. Close it instead; version switches discard dialog drafts.
    useEffect(() => {
        setDetailDialog(null);
    }, [selectedVersionId]);

    // CALL-752: respect the "Mit Extrapolation" toggle so the Kosten cost cells match the summary and
    // the Excel export — with it off, costs use entered rates only (no forward-fill, no blue tint),
    // instead of always rendering the default extrapolated values. (Snapshots are entered-only: the
    // context forces this off for them upstream.)
    const calculated = useMemo(
        () => calculateAllCosts(billingConfigs, { extrapolate: showExtrapolation }),
        [billingConfigs, showExtrapolation]
    );

    // CALL-781: `months` stays the FULL view — the row status and the extrapolation both count months,
    // so narrowing it here would turn a display toggle into a data change. Only the column list narrows.
    // CALL-815: that view is the 12-month window, or the selected date range if there is one.
    const { months, visibleMonths } = useOverviewMonths(hidePastMonths);

    const isKosten = view === 'kosten';

    // What the server last served, per config: the row build's base and the pristine value an edit is
    // measured against.
    const servedByConfig = useMemo(
        () =>
            new Map(
                billingConfigs.map((config) => [
                    config.id,
                    new Map((config.forecastVolumes || []).map((fv) => [String(fv.month), Number(fv.volume)])),
                ])
            ),
        [billingConfigs]
    );

    // The STORED source of each served month, kept apart from the row's rendered `sources` (which marks
    // pending edits 'manual'): only a stored manual row is the user's to delete.
    const servedSourceByConfig = useMemo(
        () =>
            new Map(
                billingConfigs.map((config) => [
                    config.id,
                    new Map((config.forecastVolumes || []).map((fv) => [String(fv.month), fv.source ?? 'auto'])),
                ])
            ),
        [billingConfigs]
    );

    /** The cell whose clear was just refused, so editing-stopped does not read it as an accepted
     *  projection. Cleared by the next parse. */
    const refusedClear = useRef<string | null>(null);

    // This grid's slice of the pending set, indexed for the row build and cellStyle alike.
    const pendingByConfig = useMemo(() => {
        const byConfig = new Map<number, Map<string, number | null>>();

        for (const edit of pending.edits) {
            if (edit.kind !== FORECAST_EDIT_KIND) continue;
            const forConfig = byConfig.get(edit.rowId) ?? new Map<string, number | null>();
            forConfig.set(edit.key, edit.value as number | null);
            byConfig.set(edit.rowId, forConfig);
        }

        return byConfig;
    }, [pending.edits]);

    const monthColumns = useMemo(
        () =>
            generateMonthColumns(visibleMonths, {
                minWidth: 100,
                editable: !isKosten && !isReadOnly,
                useValueFormatterForExport: false,
                valueFormatter: isKosten
                    ? // CALL-813: the Kosten view is a computed cost per config per month and is never
                      // editable (`editable` above is false whenever isKosten) — so it rounds to whole
                      // euros. The Handlingdaten view below is a whole VOLUME, not money, and is
                      // editable; integerFormatter stays.
                      (params) => formatWholeEuro(params.value)
                    : (params) => {
                          if (params.value == null || params.value === '') return '';
                          return integerFormatter.format(Number(params.value));
                      },
                ...(!isKosten && {
                    // The shared budget cell parser (German-pinned, CALL-794), rounded because a volume
                    // is a whole number of calls and the endpoint rejects a fraction.
                    valueParser: (params) => {
                        refusedClear.current = null;
                        const parsed = parseBudgetCell(params.newValue);
                        if (parsed !== null) return Math.round(parsed);

                        // CALL-779: what an emptied cell may do depends on the STORED row, never on what
                        // is rendered — `rowData` stamps every pending edit as 'manual', so reading the
                        // row's own `sources` let a pending value pass this guard for a month whose stored
                        // source is 'auto'. applyMonthDeltas then silently skipped the delete
                        // (isDeletable) and the save reported success while the number came back.
                        const field = params.colDef.field as string;
                        const rowId = params.data?.id != null ? Number(params.data.id) : null;
                        const stored = rowId != null ? servedByConfig.get(rowId)?.get(field) : undefined;

                        // The user's own override: clearing it removes the month, which the backend does.
                        if (rowId != null && servedSourceByConfig.get(rowId)?.get(field) === 'manual') return null;

                        // An auto month: clearing cannot delete it, so Delete means "undo my override" —
                        // put the stored figure back, which drops the pending edit as a restore.
                        if (stored !== undefined) return stored;

                        // Nothing stored at all (an extrapolated projection): keep what is on screen and
                        // remember the refusal, or onCellEditingStopped reads the unchanged value as
                        // "the user accepted the projection" and persists it as a manual entry.
                        refusedClear.current = `${rowId}:${field}`;
                        return params.oldValue;
                    },
                }),
                // CALL-753: editable value cells keep the yellow input background; a 'manual'
                // override additionally gets an orange left accent bar (inset box-shadow so it
                // layers over the background without shifting content, distinct from the blue used
                // elsewhere for extrapolated values). Both cues are editing-context only — a
                // snapshot is frozen (no edit, no Reload Data), so it renders plain.
                cellStyle: isKosten
                    ? (params): Record<string, string> => {
                          // CALL-752: cost cells whose rate (price/ABT) was extrapolated are light
                          // blue (#e3f2fd), matching the Prices & ABT entry grids and Strat FC.
                          const field = params.colDef?.field as string | undefined;
                          const style: Record<string, string> = { textAlign: 'right' };
                          if (field && (params.data as any)?.[`extrapolated_${field}`] === true) {
                              style.backgroundColor = EXTRAPOLATED_CELL_BG;
                          }
                          return style;
                      }
                    : (params) => {
                          const style: Record<string, string> = { textAlign: 'right' };
                          const field = params.colDef.field as string;
                          // CALL-779: an unsaved cell is bordered red on top of whatever cue it already
                          // carries — set first (in BOTH states, so a saved cell's border is unset) so it
                          // survives the early returns below.
                          const rowId = params.data?.id != null ? Number(params.data.id) : null;
                          Object.assign(
                              style,
                              pendingBorder(Boolean(field && rowId != null && pendingByConfig.get(rowId)?.has(field)))
                          );
                          // CALL-752: extrapolated (projected) future volumes are light blue (#e3f2fd),
                          // like the Prices & ABT grids and Strat FC. Display-only — takes precedence
                          // over the yellow/manual cues and shows even on a frozen snapshot.
                          if (field && (params.data as any)?.[`extrapolated_${field}`] === true) {
                              style.backgroundColor = EXTRAPOLATED_CELL_BG;
                              return style;
                          }
                          if (isReadOnly) return style;

                          style.backgroundColor = EDITABLE_CELL_BG;
                          const source = (params.data as { sources?: Record<string, string> })?.sources?.[field];
                          if (source === 'manual') {
                              style.boxShadow = MANUAL_ACCENT_SHADOW;
                          }
                          return style;
                      },
            }),
        [visibleMonths, isKosten, isReadOnly, pendingByConfig, servedByConfig, servedSourceByConfig]
    );

    const rowData = useMemo(
        () =>
            billingConfigs.map((config) => {
                const wu = config.workingUnitLocation?.workingUnit;
                const location = [config.partnerName, config.locationName].filter(Boolean).join(' - ');
                const workingUnit = [wu?.worktype?.worktype, wu?.channel?.name, wu?.task?.name]
                    .filter(Boolean)
                    .join(' - ');
                const info = rowStatus.get(config.id);
                const row: Record<string, unknown> = {
                    id: config.id,
                    // Both from the same computation, so the icon can never contradict the dialog behind
                    // it. Undefined until the period (status) and the stored acks (ackInfo) have landed —
                    // RowStatusCell then renders nothing / stays inert respectively.
                    status: info?.completeness,
                    ackInfo: info?.ackInfo,
                    // CALL-820: only an explicit false is an orphan — the flag is absent on frozen
                    // snapshot payloads (frozen history), and absence must not claim anything.
                    orphaned: config.hasSourceVolume === false,
                    location,
                    workingUnit,
                    kostenstelle: config.costCenter ?? '',
                };

                if (isKosten) {
                    const calcConfig = calculated.find((c) => c.config.id === config.id);
                    if (calcConfig) {
                        for (const [month, result] of calcConfig.monthlyResults) {
                            row[month] = result.calculatedCost;
                            // CALL-752: flag extrapolated cost cells so the column cellStyle paints them blue.
                            if (result.extrapolated) row[`extrapolated_${month}`] = true;
                        }
                    }
                } else {
                    // CALL-753: carry per-cell source so the column cellStyle can flag manual overrides.
                    const sources: Record<string, string> = {};
                    const volumeMap = new Map<string, number>();
                    for (const fv of config.forecastVolumes || []) {
                        volumeMap.set(fv.month, Number(fv.volume));
                        sources[fv.month] = fv.source ?? 'auto';
                    }

                    // CALL-779: pending edits sit on top of the served volumes, before extrapolation
                    // reads them — so an unsaved figure stops being a projection, the months after it
                    // extrapolate from it, and it survives the refetch that follows any other save on
                    // the page. It also takes the 'manual' accent right away, because that is what the
                    // batch will store; the old per-cell handler faked the same thing by mutating
                    // ag-grid's row copy and forcing a repaint.
                    for (const [month, value] of pendingByConfig.get(config.id) ?? []) {
                        if (value === null) {
                            volumeMap.delete(month);
                            delete sources[month];
                        } else {
                            volumeMap.set(month, Number(value));
                            sources[month] = 'manual';
                        }
                    }

                    if (showExtrapolation) {
                        // CALL-752: forward-extrapolate empty future volume cells (blue, display-only)
                        // over the same 6-month horizon as the Prices & ABT grids and Strat FC. Real
                        // (auto/manual) values are kept and never overwritten; extrapolated cells are
                        // never persisted. With the toggle off, show entered volumes only — no blue —
                        // matching the entered-only Kosten cells and the export's Handlingdaten sheet.
                        const { values, extrapolatedMonths } = buildExtrapolatedRow(volumeMap, months);
                        for (const [month, value] of values) row[month] = value;
                        for (const month of extrapolatedMonths) row[`extrapolated_${month}`] = true;
                    } else {
                        for (const [month, value] of volumeMap) row[month] = value;
                    }
                    row.sources = sources;
                }

                return row;
            }),
        [billingConfigs, calculated, isKosten, months, showExtrapolation, pendingByConfig, rowStatus]
    );

    // CALL-779: record the cell for the page's Save. The orange 'manual' accent and the repaint that
    // used to be done by hand here are now derived in rowData above, so a discard puts them back too.
    const recordEdit = useCallback(
        (data: Record<string, any>, monthKey: string) => {
            const configId = Number(data.id);
            // Already through the column's valueParser: a whole number of calls, or null for a cleared
            // cell (which drops the manual override, leaving the auto value to Reload Data).
            const value = data[monthKey] == null ? null : Number(data[monthKey]);
            pending.set(configId, FORECAST_EDIT_KIND, monthKey, value, servedByConfig.get(configId)?.get(monthKey));
        },
        [pending, servedByConfig]
    );

    const handleCellValueChanged = useCallback(
        (event: CellValueChangedEvent) => {
            if (isKosten) return;
            const { data, colDef } = event;
            if (!data?.id || !colDef.field || !MONTH_KEY.test(colDef.field)) return;
            recordEdit(data, colDef.field);
        },
        [isKosten, recordEdit]
    );

    // CALL-752: ag-grid v32 skips the save when the typed value equals the cell's current value, so
    // "accept the projected volume as-is" (open a blue cell, Enter) never fires onCellValueChanged.
    // Catch it here: editing stopped with NO change, NOT cancelled (Esc/clear yields newValue == null),
    // on a display-only extrapolated cell → record it as a real (manual) entry.
    const onCellEditingStopped = useCallback(
        (event: CellEditingStoppedEvent) => {
            if (isKosten || event.valueChanged || event.newValue == null) return;
            const { data, colDef } = event;
            const field = colDef.field;
            if (!data?.id || !field || !MONTH_KEY.test(field)) return;
            // A clear the parser refused looks exactly like an accepted projection from here — unchanged
            // value, non-null. Persisting it would store the projection the user was trying to erase.
            if (refusedClear.current === `${Number(data.id)}:${field}`) {
                refusedClear.current = null;
                return;
            }
            if ((data as Record<string, unknown>)[`extrapolated_${field}`] !== true) return;
            recordEdit(data as Record<string, any>, field);
        },
        [isKosten, recordEdit]
    );

    // CALL-752: ag-grid skips re-running cellStyle when a cell's value is unchanged — a volume cell can
    // flip extrapolated(blue)<->entered without its value changing (e.g. after a save/refetch), leaving
    // a stale background. Force a repaint on data change.
    useEffect(() => {
        gridRef.current?.api?.refreshCells({ force: true });
    }, [rowData, pendingByConfig]);

    // CALL-818: open the month editor for the clicked row, seeded with the page's unsaved volume
    // edits for it — so the dialog shows what the screen shows, and its Save persists them too.
    const handleDetailClick = useCallback(
        (configId: number) => {
            const config = billingConfigs.find((candidate) => candidate.id === configId);
            if (!config) return;
            setDetailDialog({ config, pendingVolumes: pendingByConfig.get(configId) });
        },
        [billingConfigs, pendingByConfig]
    );

    // CALL-820: the orphaned row's plain cells take the pale red wash (month cells keep their own
    // editable/extrapolated backgrounds on top — cell styles paint over the row style). A COVERED
    // acknowledgement calms the wash together with the icon (see RowStatusCell: the green is
    // self-correcting — coverage breaks as soon as reload empties more months than were accepted).
    // The healthy branch resets EXPLICITLY: ag-grid reuses row elements via getRowId, so an omitted
    // key would leave the wash on whatever row inherits it.
    const getRowStyle = useCallback((params: { data?: { orphaned?: boolean; ackInfo?: RowAckInfo } }) => {
        const acked = Boolean(params.data?.ackInfo?.ack && params.data?.ackInfo?.covered);
        return params.data?.orphaned && !acked ? { backgroundColor: ORPHANED_ROW_BG } : { backgroundColor: '' };
    }, []);

    // Second entry: double-clicking anywhere on the row that is NOT a month cell (a month cell's
    // double-click starts the inline editor) opens the same dialog.
    const handleCellDoubleClicked = useCallback(
        (event: CellDoubleClickedEvent) => {
            const field = event.colDef?.field;
            if (!event.data?.id || (field && MONTH_KEY.test(field))) return;
            handleDetailClick(Number(event.data.id));
        },
        [handleDetailClick]
    );

    const identifierColumns: ColDef[] = useMemo(
        () => [
            {
                headerName: '',
                field: 'status',
                width: 52,
                minWidth: 52,
                maxWidth: 52,
                editable: false,
                sortable: false,
                filter: false,
                resizable: false,
                cellRenderer: RowStatusCell,
                cellRendererParams: {
                    // CALL-818: the icon opens the month-editor dialog, where the CALL-816 warning is
                    // also acknowledged — live in every state; ackEnabled only picks the hint wording.
                    onAckClick: (data: Record<string, unknown>) => handleDetailClick(Number(data.id)),
                    ackEnabled: !isReadOnly,
                    periodLabel,
                },
                // Icon-only cell: flex-centre both axes — left to the theme's line-height the icon
                // hangs on the TEXT baseline and sits visibly above the row's vertical centre.
                cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 },
                headerTooltip: t('field.status'),
            },
            { headerName: t('field.location'), field: 'location', minWidth: 200, editable: false },
            { headerName: t('field.workingUnit'), field: 'workingUnit', minWidth: 260, editable: false },
            { headerName: t('field.costCenter'), field: 'kostenstelle', minWidth: 130, editable: false },
        ],
        [t, handleDetailClick, isReadOnly, periodLabel]
    );

    const columnDefs = useMemo(() => [...identifierColumns, ...monthColumns], [identifierColumns, monthColumns]);

    const defaultColDef: ColDef = useMemo(
        () => ({
            sortable: true,
            filter: true,
            resizable: true,
        }),
        []
    );

    return (
        <>
            <TableGridItem
                ref={gridRef}
                rowData={rowData}
                getRowStyle={getRowStyle}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                onCellValueChanged={handleCellValueChanged}
                onCellEditingStopped={onCellEditingStopped}
                onCellDoubleClicked={handleCellDoubleClicked}
                getRowId={(params) => String(params.data.id)}
                loading={isLoading}
                enableRangeSelection
                height={'auto'}
            />
            <ConfigDetailDialog
                data={detailDialog}
                versionId={selectedVersionId}
                onClose={() => setDetailDialog(null)}
                // The dialog just wrote these months, superseding the page's unsaved edits for them —
                // drop exactly those cells of this grid's kind. Untouched months keep their page
                // edits, and the kind scoping keeps baseline/on-top rows (independent id sequences)
                // out of reach.
                onSaved={(configId, savedMonths) => pending.dropCells(configId, FORECAST_EDIT_KIND, savedMonths)}
            />
        </>
    );
};

export default ForecastVolumeGrid;
